import { BoxProps } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ContactRequestSchema,
  ContactStatusEnum,
  IContactRequestSchema,
  IContactRespSchema,
} from '@monetize/types/app';
import { forwardRef, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  useCreateContact,
  useUpdateContact,
} from '../../../../../api/contactsService';
import { useGetById } from '../../../../../api/queryUtils';
import { ContactForm } from '../../../../../components/Contacts/ContactForm';
import { MBox, MButton } from '../../../../../components/Monetize';
import { getCountryCodeFromLanguage } from '../../../../../services/countryState';
import { logger } from '../../../../../services/logger';

export interface QuoteContactFormProps extends BoxProps {
  accountId: string;
  isInternal?: boolean;
  isCcContact?: boolean;
  isCompact?: boolean;
  existingContact?: Partial<IContactRespSchema> | null;
  onClose: (contact?: IContactRespSchema) => void;
  formId?: string;
}

export const QuoteContactForm = forwardRef<any, QuoteContactFormProps>(
  (
    {
      accountId,
      isInternal,
      isCcContact = false,
      isCompact = true,
      existingContact,
      onClose,
      formId,
      ...rest
    }: QuoteContactFormProps,
    ref,
  ) => {
    const formDefault: Partial<IContactRequestSchema> = {
      snapshotId: existingContact?.snapshotId ?? null,
      title: existingContact?.title ?? '',
      fullName: existingContact?.fullName || '',
      email: existingContact?.email || '',
      phone: existingContact?.phone || null,
      customId: existingContact?.customId || null,
      address: existingContact?.address || {
        city: null,
        country: null,
        line1: null,
        line2: null,
        state: null,
        postalCode: null,
      },
      status: existingContact?.status || ContactStatusEnum.ACTIVE,
      internal: existingContact?.internal ?? isInternal,
      primary: existingContact?.primary ?? false,
      customFields: {},
    };

    const { isLoading: isContactFetchLoading, data: contact } =
      useGetById<IContactRespSchema>('contacts', existingContact?.id!, {
        enabled: !!existingContact?.id,
        refetchOnWindowFocus: false,
        select: (data) => {
          if (data.address && !data.address.country && !data.address.state) {
            data.address.country = getCountryCodeFromLanguage();
          }
          return data;
        },
      });
    const formMethods = useForm<IContactRequestSchema>({
      resolver: zodResolver(ContactRequestSchema),
      mode: 'onBlur',
      defaultValues: contact || formDefault,
    });
    const {
      handleSubmit,
      control,
      formState: { errors },
      trigger,
      setValue,
      watch,
      reset,
    } = formMethods;

    useEffect(() => {
      if (contact) {
        reset(Object.assign({}, formDefault, contact));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contact]);

    const isValid = Object.keys(errors).length === 0;

    const { mutate: doCreateContact, isPending: saveLoading } =
      useCreateContact({
        onSuccess: (response) => onClose(response),
        meta: { showErrorToast: true },
      });

    const { mutate: doUpdateContact, isPending: updateLoading } =
      useUpdateContact({
        onSuccess: (response) => onClose(response),
        meta: { showErrorToast: true },
      });

    const isLoading =
      saveLoading ||
      updateLoading ||
      (existingContact && isContactFetchLoading);

    const onSubmit = async (payload: IContactRequestSchema) => {
      if (!isCcContact && payload?.internal) {
        payload.esigner = true;
      } else if (!isCcContact && !payload?.internal) {
        payload.esigner = false;
      }
      if (existingContact?.id) {
        doUpdateContact({
          contactId: existingContact.id,
          payload,
        });
      } else {
        doCreateContact({
          payload,
          accountId,
        });
      }
    };

    const onError = (errs: any, event: any) => {
      logger.warn(`ERRORS:`, errs);
    };

    return (
      <MBox {...rest}>
        <MBox
          onKeyDownCapture={(e) => {
            if (e.key !== 'Enter') {
              return;
            }
            // Don't hijack textarea enter
            if ((e.target as HTMLElement)?.tagName === 'TEXTAREA') {
              return;
            }
            // Prevent the outer (parent) form from submitting when editing a contact inline
            e.preventDefault();
            e.stopPropagation();
            handleSubmit(onSubmit, onError)();
          }}
        >
          <FormProvider {...formMethods}>
            <ContactForm
              isCompact={isCompact}
              isCompactForCcContact={isCcContact}
              omitPrimary
              formId={formId || 'contact-form'}
              isDisabled={!!isLoading}
              control={control}
              errors={errors}
              trigger={trigger}
              watch={watch}
              setValue={setValue}
              handleSubmit={handleSubmit(onSubmit, onError)}
              contactId={existingContact?.id}
            >
              <MButton
                data-testid="contact-form-cancel-button"
                variant="cancel"
                type="button"
                size="sm"
                mr="2"
                isDisabled={!!isLoading}
                onClick={() => onClose()}
              >
                Cancel
              </MButton>
              <MButton
                data-testid="contact-form-submit"
                variant="primary"
                type="button"
                size="sm"
                isLoading={!!isLoading}
                isDisabled={!isValid || !!isLoading}
                onClick={() => handleSubmit(onSubmit, onError)()}
              >
                {existingContact?.id ? 'Update' : 'Add'}
              </MButton>
            </ContactForm>
          </FormProvider>
        </MBox>
      </MBox>
    );
  },
);
